/* assets/confetti-cursor.js
   Cursor flower trail using external PNG URLs (Shopify Files CDN)
   - No theme asset uploads required
   - Uses window.CURSOR_FLOWER_URLS = [url1, url2, url3, url4]
   - Falls back to drawn flower if sprites aren't ready
*/
(() => {
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const isTouch =
    "ontouchstart" in window ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);

  if (reduceMotion || isTouch) return;

  // ---- TUNE ----
  const MAX_PARTICLES = 170;
  const SPAWN_DISTANCE = 10;       // px moved before spawning again
const PARTICLES_PER_SPAWN = 1;
  const SIZE_MIN = 14;
  const SIZE_MAX = 22;
  // -------------

  const SPRITE_URLS = Array.isArray(window.CURSOR_FLOWER_URLS)
    ? window.CURSOR_FLOWER_URLS.filter(Boolean)
    : [];

  // If no URLs are provided, we still run with the fallback drawn flowers
  const HAVE_SPRITES = SPRITE_URLS.length > 0;

  // Canvas
  const canvas = document.createElement("canvas");
  canvas.id = "confetti-cursor-canvas";
  canvas.style.cssText = `
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 2147483647;
  `;

  const mount = () => (document.body || document.documentElement).appendChild(canvas);
  if (document.body) mount();
  else window.addEventListener("DOMContentLoaded", mount, { once: true });

  const ctx = canvas.getContext("2d", { alpha: true });
  const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

  function resize() {
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in CSS pixels
  }
  resize();
  window.addEventListener("resize", resize, { passive: true });

  // Utils
  const particles = [];
  const rand = (min, max) => min + Math.random() * (max - min);
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];

  // Simple fallback palette (used only if sprites not loaded)
  const FALLBACK_PALETTE = ["#CFEFC8", "#FFF5B8", "#B238C6", "#E7D8FF"];

  // Preload sprites
  const sprites = HAVE_SPRITES
    ? SPRITE_URLS.map((url) => {
        const img = new Image();
        img.decoding = "async";
        img.loading = "eager";
        img.src = url;
        return img;
      })
    : [];

  // Debug help (optional): uncomment if needed
  // console.log("[cursor-flowers] sprite urls:", SPRITE_URLS);

  function spriteReady(img) {
    return !!(img && img.complete && img.naturalWidth > 0);
  }

  function spawn(x, y, vx, vy) {
    const img = HAVE_SPRITES ? pick(sprites) : null;

    particles.push({
      x,
      y,
      vx: vx * 0.10 + rand(-0.5, 0.5),
      vy: vy * 0.10 + rand(-0.3, 0.8),
      g: rand(0.015, 0.04),
      r: rand(0, Math.PI * 2),
      vr: rand(-0.10, 0.10),

      size: rand(SIZE_MIN, SIZE_MAX),

      life: 1,
      fade: rand(0.010, 0.018),

      img,
      // fallback colors if sprite not ready
      petal: pick(FALLBACK_PALETTE),
      center: "#000000",
    });

    if (particles.length > MAX_PARTICLES) {
      particles.splice(0, particles.length - MAX_PARTICLES);
    }
  }

  function drawFallbackFlower(p) {
    // stylized 5-petal flower
    const petals = 5;
    const petalR = p.size * 0.52;
    const centerR = Math.max(1.8, p.size * 0.18);

    ctx.fillStyle = p.petal;
    for (let k = 0; k < petals; k++) {
      const a = (k / petals) * Math.PI * 2;
      const px = Math.cos(a) * petalR * 0.55;
      const py = Math.sin(a) * petalR * 0.55;

      ctx.beginPath();
      ctx.ellipse(px, py, petalR * 0.55, petalR * 0.35, a, 0, Math.PI * 2);
      ctx.fill();
    }

    // center dot (black like your mark details)
    ctx.fillStyle = p.center;
    ctx.beginPath();
    ctx.arc(0, 0, centerR, 0, Math.PI * 2);
    ctx.fill();
  }

  // Pointer tracking (process latest event in rAF)
  let lastX = null,
    lastY = null;
  let lastSpawnX = null,
    lastSpawnY = null;
  let lastT = performance.now();
  let pending = null;

  window.addEventListener(
    "pointermove",
    (e) => {
      if (e.pointerType === "touch") return;
      pending = e;
    },
    { passive: true }
  );

  function tick(now) {
    const dt = Math.min(32, now - lastT);
    lastT = now;

    if (pending) {
      const x = pending.clientX;
      const y = pending.clientY;

      if (lastX == null) {
        lastX = lastSpawnX = x;
        lastY = lastSpawnY = y;
      }

      const dx = x - lastX;
      const dy = y - lastY;

      const sdx = x - lastSpawnX;
      const sdy = y - lastSpawnY;
      const dist = Math.hypot(sdx, sdy);

      if (dist >= SPAWN_DISTANCE) {
        for (let i = 0; i < PARTICLES_PER_SPAWN; i++) spawn(x, y, dx, dy);
        lastSpawnX = x;
        lastSpawnY = y;
      }

      lastX = x;
      lastY = y;
      pending = null;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];

      p.vy += p.g * (dt / 16);
      p.x += p.vx * (dt / 16);
      p.y += p.vy * (dt / 16);
      p.r += p.vr * (dt / 16);
      p.life -= p.fade * (dt / 16);

      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }

      ctx.save();
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.translate(p.x, p.y);
      ctx.rotate(p.r);

      // Draw sprite if ready, else fallback flower
      if (p.img && spriteReady(p.img)) {
        const s = p.size;
        ctx.drawImage(p.img, -s / 2, -s / 2, s, s);
      } else {
        drawFallbackFlower(p);
      }

      ctx.restore();
    }

    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
})();
